import { ApiError } from '@clockwise/shared';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usersApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { userDisplayName } from '@/lib/user-display';
import { Section } from './section';

/**
 * Direct reports of a single employee (users whose `manager_id` is this
 * person), rendered on the user detail page below Customer Teams.
 *
 * Self-hiding: an employee who manages no one renders NOTHING (no empty
 * section) — the parent always mounts this and it collapses to `null` once the
 * query resolves empty. A LOAD FAILURE is NOT hidden (G15): it surfaces an
 * error section so the user can tell "no reports" from "couldn't load".
 *
 * The list is a tenant-scoped relationship view (names + title), not
 * access-scoped — the same treatment as the manager-name label on this page.
 */
interface Props {
  userId: string;
}

export function UserDirectReportsSection({ userId }: Props) {
  const reports = useQuery({
    queryKey: queryKeys.users.directReports(userId),
    queryFn: () => usersApi.listDirectReports(userId),
  });

  // While the query is in flight we render nothing — this is a secondary
  // section, so a skeleton would just add layout noise for the common case
  // (most employees manage no one).
  if (reports.isPending) return null;

  if (reports.isError) {
    return (
      <Section title="Direct reports">
        <p className="text-sm text-destructive">
          {reports.error instanceof ApiError
            ? reports.error.message
            : 'Failed to load direct reports.'}
        </p>
      </Section>
    );
  }

  const rows = reports.data.data;
  if (rows.length === 0) return null;

  return (
    <Section
      title="Direct reports"
      description="Employees who report to this person."
      count={rows.length}
    >
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Title</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id}>
                <TableCell>
                  <Link
                    to="/users/$id"
                    params={{ id: r.id }}
                    className="font-medium text-primary underline-offset-2 hover:underline"
                  >
                    {userDisplayName(r)}
                  </Link>
                </TableCell>
                <TableCell className="text-sm">
                  {r.title ?? <span className="text-muted-foreground">—</span>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Section>
  );
}
